import { Link } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { OwnersResponseType } from '@/shared/backend/types/leaderboard'

import Pagination from 'components/Pagination/Pagination'
import { Typography } from 'components/Typography/Typography'
import { ellipsizeTokenAddress } from 'utils/helpers'
import { getPctValue } from 'utils/pctFormatter'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { TableContentLoader } from '../components/TableContentLoader'
import { IconInfo } from 'components/Icons/InfoIcon'
import { OwnersResponseTypeWithRank } from '../hooks/useLeaderboardOwners'

const Container = styled.div`
  margin-top: 24px;
`

const ListItem = styled.div`
  border: 1px solid ${p => p.theme.colors.divider};
  padding: 12px 8px;
  margin-bottom: 8px;
  border-radius: 16px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 6px;
  border-bottom: 1px solid ${p => p.theme.colors.divider};
`

const Row = styled.div`
  padding-top: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Description = styled(Typography)`
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    margin-right: 12px;
  }

  margin-bottom: 12px;
`

interface Props {
  paginatedItems: OwnersResponseTypeWithRank[]
  currentPage: number
  totalPages: number
  handlePageChange: (page: number) => void
  isLoading: boolean
}

export const WalletsList = ({ paginatedItems, currentPage, totalPages, handlePageChange, isLoading }: Props) => {
  const theme = useTheme()

  if (isLoading) {
    return (
      <Container>
        <TableContentLoader />
      </Container>
    )
  }

  return (
    <Container>
      <Description variant="caption">
        <IconInfo />
        <span style={{ textAlign: 'left' }}>
          Open equity shows current positions. All other metrics (PnL, ROI, HODL benchmark, Positions) use closed
          positions only.
        </span>
      </Description>
      {paginatedItems.map(d => (
        <ListItem key={d.ownerId}>
          <Header>
            <Typography variant="caption" fontWeight="semiBold">
              #{d.rank}
            </Typography>
            <Link
              to={`https://suivision.xyz/account/${d.ownerId}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: theme.colors.text.primary }}
            >
              <Typography variant="caption" fontWeight="semiBold">
                {ellipsizeTokenAddress(d.ownerId)}
              </Typography>
            </Link>
          </Header>
          <Row>
            <Typography variant="caption">Open equity:</Typography>
            <Typography variant="caption">{getFormattedUsdValue(d.openEquity)}</Typography>
          </Row>
          <Row>
            <Typography variant="caption">Positions:</Typography>
            <Typography variant="caption">{d.totalPositions}</Typography>
          </Row>
          <Row>
            <Typography variant="caption">PnL:</Typography>
            <Typography
              variant="caption"
              color={typeof d.totalPnl !== 'number' ? 'primary' : d.totalPnl > 0 ? 'success' : 'danger'}
            >
              {typeof d.totalPnl === 'number' ? getFormattedUsdValue(d.totalPnl) : '-'}
            </Typography>
          </Row>
          <Row>
            <Typography variant="caption">ROI:</Typography>
            <Typography
              variant="caption"
              color={
                typeof d.totalReturnOnEquity !== 'number' ? 'primary' : d.totalReturnOnEquity > 0 ? 'success' : 'danger'
              }
            >
              {typeof d.totalReturnOnEquity === 'number' ? getPctValue(d.totalReturnOnEquity) : '-'}
            </Typography>
          </Row>
          <Row>
            <Typography variant="caption">HODL:</Typography>
            <Typography variant="caption">
              {typeof d.totalHodlBenchmarkPnl === 'number' ? getFormattedUsdValue(d.totalHodlBenchmarkPnl) : '-'}
            </Typography>
          </Row>
        </ListItem>
      ))}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        type="leaderboard-wallets"
      />
    </Container>
  )
}
